/** /academy/legal/:page — terms of sale, privacy, refunds, cookies (data of the agency, FR/EN). */

import { useAcademyLegalRetrieve } from "@agencyos/api-client";
import { Skeleton } from "@agencyos/ui-web";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useParams } from "react-router-dom";

import { dateText } from "../shared";

export const LEGAL_PAGES = ["terms", "privacy", "refund", "cookies"] as const;

interface Section {
  title: string;
  body: string;
}

export function LegalPage() {
  const { t, i18n } = useTranslation();
  const { page = "terms" } = useParams();
  const language = i18n.resolvedLanguage === "en" ? "en" : "fr";
  const legal = useAcademyLegalRetrieve(page, { lang: language }, { query: { retry: false } });
  const data = legal.data?.status === 200 ? legal.data.data : undefined;
  const sections = (Array.isArray(data?.sections) ? data.sections : []) as Section[];
  return (
    <div className="ac-page as-legal">
      <nav className="as-legal__nav" aria-label={t("academySales.account.legal")}>
        {LEGAL_PAGES.map((key) => (
          <NavLink key={key} to={`/academy/legal/${key}`} className={({ isActive }) => `as-legal__link${isActive ? " as-legal__link--on" : ""}`}>
            {t(`academySales.legal.${key}`)}
          </NavLink>
        ))}
      </nav>
      {legal.isLoading ? <Skeleton style={{ height: 320 }} /> : null}
      {legal.isError ? <p className="ko">{t("common.error")}</p> : null}
      {data ? (
        <article className="ac-surface ac-card as-legal__body" lang={language}>
          <header>
            <h1 className="ac-title-1">{data.title}</h1>
            <p className="ac-footnote">{t("academySales.legal.version", { version: dateText(data.version, i18n.language) })}</p>
            {data.review_needed ? <p className="ac-footnote">{t("academySales.legal.review")}</p> : null}
          </header>
          {sections.map((section) => (
            <section key={section.title}>
              <h2 className="ac-title-3">{section.title}</h2>
              <p>{section.body}</p>
            </section>
          ))}
        </article>
      ) : null}
      <Link className="ac-link" to="/academy">
        {t("academySales.legal.back")}
      </Link>
    </div>
  );
}
